import { Icon } from "@/shared/ui/icon"

export type SimulatorContent = {
  task: string
  steps: readonly { title: string; detail: string }[]
  deliverables: readonly { name: string; format: string }[]
}

export const ATLAS_DEMO_ID = "atlas-demo"

const fileToneByFormat: Record<string, string> = {
  PDF: "from-rose-400/25 to-white/[0.04] text-rose-100",
  XLSX: "from-emerald-400/25 to-white/[0.04] text-emerald-100",
  PPTX: "from-amber-400/25 to-white/[0.04] text-amber-100",
}

export default function TaskSimulatorStatic({ content }: { content: SimulatorContent }) {
  return (
    <div
      id={ATLAS_DEMO_ID}
      tabIndex={-1}
      aria-label="Демонстрация работы Атласа"
      className="atlas-demo-shell scroll-mt-20 overflow-hidden rounded-[1.75rem] border border-violet-300/45 bg-[#151515] text-white shadow-2xl shadow-violet-300/30 outline-none dark:border-white/15 dark:shadow-violet-950/40"
    >
      <div className="grid h-12 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b border-white/10 px-4 sm:px-5">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="size-2.5 rounded-full bg-[#ff5f57]" />
          <span aria-hidden="true" className="size-2.5 rounded-full bg-[#febc2e]" />
          <span aria-hidden="true" className="size-2.5 rounded-full bg-[#28c840]" />
        </div>
        <p className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-center text-2xs font-semibold text-white/75 sm:text-sm">
          Отчёт по продажам<span className="hidden sm:inline"> за август</span>
        </p>
        <span className="inline-flex items-center rounded-full bg-white/8 px-2.5 py-1 font-mono text-2xs text-white/55">
          AI: Auto
        </span>
      </div>

      <div className="grid h-[min(540px,calc(100dvh-9rem))] min-h-[430px] md:grid-cols-[12rem_minmax(0,1fr)] lg:grid-cols-[13.5rem_minmax(0,1fr)]">
        <aside className="hidden border-r border-white/10 bg-black/15 p-4 md:flex md:flex-col">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span className="flex size-8 items-center justify-center rounded-lg bg-brand-500 text-xs">
              W
            </span>
            WEBDAD
          </div>
          <p className="mt-8 px-2 type-overline text-white/40">Недавние задачи</p>
          <ul className="mt-3 space-y-1 text-xs text-white/45">
            <li className="rounded-lg bg-white/8 px-2.5 py-2.5 text-white/85">
              Продажи · август
            </li>
            <li className="px-2.5 py-2.5">Подготовка к встрече</li>
            <li className="px-2.5 py-2.5">Сводка по проектам</li>
          </ul>
          <p className="mt-auto px-2 text-xs text-white/40">
            Всё начинается с обычного сообщения
          </p>
        </aside>

        <div className="flex min-w-0 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-3 py-3 [scrollbar-width:none] sm:px-7 sm:py-5 lg:px-10 [&::-webkit-scrollbar]:hidden">
            <div className="atlas-user-message flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-white">Пользователь</span>
                <span className="font-mono text-2xs text-[#66718a]">12:45</span>
                <span className="atlas-user-avatar flex size-6 items-center justify-center rounded-full bg-[#3b414b] text-2xs font-semibold text-white">
                  U
                </span>
              </div>
              <div className="max-w-[92%] rounded-[1.125rem] rounded-tr-md border border-white/[0.07] bg-[#23262f] px-4 py-3 text-sm font-medium text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.04)] sm:max-w-[84%]">
                {content.task}
              </div>
            </div>

            <div className="atlas-agent-run mt-4 flex items-start gap-2 sm:gap-3">
              <span className="atlas-agent-avatar hidden size-8 shrink-0 items-center justify-center rounded-full bg-[#3b414b] text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18)] sm:flex">
                <span className="block h-3 w-4 text-center text-2xs font-semibold leading-none text-current">
                  wJ
                </span>
              </span>
              <section className="min-w-0 flex-1 overflow-hidden rounded-[1.125rem] border border-white/10 bg-[#242424] text-white">
                <header className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b border-white/10 px-3.5 py-3 sm:px-4">
                  <span className="atlas-agent-run-icon flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-slate-950">
                    <Icon name="checklist-minimalistic-linear" className="size-4.5" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold leading-none">Ход агента</p>
                      <span className="text-xs font-medium text-emerald-300">Готово</span>
                    </div>
                    <p className="mt-1 truncate font-mono text-2xs text-[#6d7280]">
                      {content.steps.length} шагов · источники проверены · 12:45
                    </p>
                  </div>
                  <Icon
                    name="alt-arrow-up-linear"
                    className="size-4 shrink-0 text-[#8da0ba]"
                  />
                </header>

                <ol className="px-3.5 py-2 sm:px-4">
                  {content.steps.map((step, index) => (
                    <li
                      key={`${step.title}-${index}`}
                      className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-x-2 rounded-xl px-2 py-2"
                    >
                      <span className="mt-1.5 flex size-3 shrink-0 items-center justify-center rounded-full bg-emerald-400/25 text-emerald-300">
                        <Icon name="check-read-linear" className="size-2" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-2xs font-medium text-[#9da6ba]">
                          Шаг {index + 1}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-white">
                          {step.title}
                        </p>
                        <p className="pt-1 text-xs text-white/72">{step.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </div>

            <div className="atlas-deliverables mt-3 grid gap-2 rounded-2xl border border-white/10 bg-black/10 p-3 sm:ml-11 sm:grid-cols-3">
              {content.deliverables.map((file) => (
                <div
                  key={file.name}
                  className="flex min-w-0 items-center gap-2.5 rounded-xl border border-white/10 bg-gradient-to-br p-2.5 shadow-[inset_0_1px_0_rgb(255_255_255/0.05)]"
                >
                  <span
                    className={`flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br font-mono text-2xs font-semibold ${fileToneByFormat[file.format] ?? "from-violet-400/25 to-white/[0.04] text-violet-100"}`}
                  >
                    {file.format}
                  </span>
                  <span className="min-w-0 text-2xs font-medium text-white/68">
                    {file.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="atlas-demo-input-wrap border-t border-white/10 px-3 py-2.5 sm:px-5 sm:py-3 lg:px-7">
            <div className="atlas-input-bar rounded-[1.35rem] border bg-[#242424] px-4 py-3 sm:rounded-[1.4rem] sm:py-3.5">
              <div className="grid min-h-16 grid-rows-[1fr_auto] gap-2.5 sm:min-h-18 sm:gap-3">
                <p className="line-clamp-2 min-h-9 min-w-0 overflow-hidden text-sm font-medium text-[#aab4c7] sm:min-h-10">
                  Спросите что-нибудь...
                </p>
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <button
                    type="button"
                    aria-label="Прикрепить файл"
                    className="flex size-11 shrink-0 items-center justify-center text-[#aab4c7] transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                  >
                    <Icon name="paperclip-linear" className="size-4.5" />
                  </button>
                  <button
                    type="button"
                    className="hidden shrink-0 items-center gap-1.5 text-xs font-medium text-white transition hover:text-white/80 sm:flex"
                  >
                    <span>Высокий</span>
                    <Icon
                      name="alt-arrow-down-linear"
                      className="size-3 text-[#aab4c7]"
                    />
                  </button>
                  <button
                    type="button"
                    aria-label="Отправить"
                    className="ml-auto flex size-11 shrink-0 items-center justify-center rounded-full bg-[#777777] text-[#181818] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                  >
                    <Icon name="arrow-up-linear" className="size-4 sm:size-4.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
